import SpaceTop, { SpaceIntro } from '@ui/elements/Space'
import { presets, Stage } from '@shell/Topbar/types'
import { useNavigation, usePathname } from 'expo-router'
import useAvatarFlow from 'features/avatar/useAvatarFlow'
import useIntroStore from 'hooks/useIntroStore'
import { useIsFocused } from '@ui/hooks/useIsFocused'
import useSession, { useStaff } from 'hooks/useSession'
import { Back, useStagesStore } from 'hooks/useStagesStore'
import React, { useEffect, useRef } from 'react'
import { Freeze } from 'react-freeze'
import {
  Easing,
  Platform,
  ScrollView,
  ScrollViewProps,
  View,
} from 'react-native'
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated'
import cn from '@ui/utils/cn'

interface Props {
  /** @deprecated use stage instead */
  title?: string
  stage?: Stage
  children?: React.ReactNode
  className?: string
  list?: boolean
  back?: Back
  intro?: boolean
  dropdown?: string
  disableScroll?: boolean
  validateStaff?: boolean
  scrollViewRef?: React.RefObject<Animated.ScrollView | null>
  onScroll?: ScrollViewProps['onScroll']
}

// TODO: Rewrite this to use useFocusEffect from expo-router
const usePageFocus = ({ stage, dropdown, back, intro }: Props) => {
  const isFocused = useIsFocused()
  const setStage = useStagesStore((state) => state.setStage)
  const setBack = useStagesStore((state) => state.setBack)
  const setIsLogo = useStagesStore((state) => state.setIsLogo)
  const { isAvatarFlow } = useAvatarFlow()

  const preset =
    stage && presets['main'].includes(stage)
      ? isAvatarFlow
        ? 'avatar'
        : 'main'
      : undefined

  useEffect(() => {
    if (!isFocused) {
      return
    }
    if (stage !== undefined) {
      setStage(stage, preset, dropdown)
    }
    if (!stage) {
      setStage(null, preset, dropdown)
    }
  }, [setStage, isFocused, stage, preset, dropdown])

  useEffect(() => {
    if (!isFocused) {
      return
    }
    setBack(back !== undefined ? back : true) // WARN: keep default value to true unless overridden
  }, [isFocused, back, setBack])

  useEffect(() => {
    if (!isFocused) {
      return
    }
    setIsLogo(intro !== undefined ? intro : false)
  }, [isFocused, intro, setIsLogo])

  const navigation = useNavigation()
  const setPrevScreen = useIntroStore((s) => s.setPrevScreen)
  const pathname = usePathname()

  useEffect(() => {
    if (!isFocused) {
      return
    }

    const listener = navigation.addListener('beforeRemove', (e) => {
      if (e.data.action.type === 'GO_BACK' || e.data.action.type === 'POP') {
        setPrevScreen(pathname)
      } else {
        setPrevScreen(undefined)
      }
    })

    return () => {
      listener()
    }
  }, [navigation, pathname, setPrevScreen])
}

function PageBase({
  children,
  className,
  list,
  intro,
  disableScroll,
  scrollViewRef,
  onScroll,
}: Props) {
  if (list)
    return <View className={cn('flex-1 bg-white', className)}>{children}</View>

  return (
    <>
      <Animated.ScrollView
        ref={scrollViewRef}
        className={cn('h-full flex-1 bg-white', className)}
        contentContainerClassName="web:min-h-full"
        keyboardShouldPersistTaps="handled"
        scrollEnabled={!disableScroll}
        scrollEventThrottle={16}
        onScroll={onScroll}
      >
        <SpaceTop />
        {intro && <SpaceIntro />}
        {children}
      </Animated.ScrollView>
    </>
  )
}

const FreezeBlurred = ({ children }: { children: React.ReactNode }) => {
  const isFocused = useIsFocused()
  return <Freeze freeze={!isFocused}>{children}</Freeze>
}

const ValidateStaff = () => {
  const staff = useStaff()
  const staffRevalidate = useSession((s) => s.staffRevalidate)
  const toggleMode = useIntroStore((s) => s.toggleMode)
  const mode = useIntroStore((s) => s.mode)

  const navigation = useNavigation()

  // Focus effect fires to often for some reason
  useEffect(() => {
    const listener = navigation.addListener('focus', (e) => {
      if (!staff || !staffRevalidate || mode === 'staff') {
        return
      }
      toggleMode('staff', { goBack: true })
    })
    return () => {
      listener()
    }
  }, [staff, staffRevalidate, mode, toggleMode, navigation])

  return null
}

export default function Page(props: Props) {
  usePageFocus(props)

  if (Platform.OS === 'web')
    return (
      <>
        <Animated.View
          className="h-full flex-1 bg-white"
          entering={FadeIn.duration(300).easing(Easing.in(Easing.ease))}
          exiting={FadeOut.duration(300)}
        >
          <FreezeBlurred>
            <PageBase {...props} />
          </FreezeBlurred>
        </Animated.View>
        {props.validateStaff && <ValidateStaff />}
      </>
    )

  return (
    <>
      <PageBase {...props} />
      {props.validateStaff && <ValidateStaff />}
    </>
  )
}
